import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  startRequesting,
  type AccessShareRequesterHandle,
} from "@/lib/accessShareClient";
import {
  decryptAccessKey,
  exportPublicKey,
  generateEcdhKeyPair,
  importPublicKey,
} from "@/lib/accessShareCrypto";

type RequestStatus = "idle" | "requesting" | "waiting";

export function AccessPage() {
  const navigate = useNavigate();
  const [key, setKey] = useState("");
  const [status, setStatus] = useState<RequestStatus>("idle");
  const [pairingCode, setPairingCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const requesterRef = useRef<AccessShareRequesterHandle | null>(null);
  const keyPairRef = useRef<CryptoKeyPair | null>(null);
  const startingRef = useRef(false);
  const cancelledRef = useRef(false);

  const resetRequest = useCallback(() => {
    cancelledRef.current = true;
    requesterRef.current?.cancel();
    requesterRef.current = null;
    keyPairRef.current = null;
    setStatus("idle");
    setPairingCode("");
  }, []);

  useEffect(() => {
    return () => {
      cancelledRef.current = true;
      requesterRef.current?.cancel();
      requesterRef.current = null;
      keyPairRef.current = null;
    };
  }, []);

  const handleRequestKey = useCallback(async () => {
    if (startingRef.current || requesterRef.current) return;
    startingRef.current = true;
    cancelledRef.current = false;
    setError(null);
    setStatus("requesting");
    try {
      const keyPair = await generateEcdhKeyPair();
      if (cancelledRef.current) return;
      keyPairRef.current = keyPair;
      const publicKey = await exportPublicKey({ key: keyPair.publicKey });
      if (cancelledRef.current) return;
      const handle = startRequesting({
        publicKey,
        onWaiting: ({ pairingCode: code }) => {
          setPairingCode(code);
          setStatus("waiting");
        },
        onGranted: ({ publicKeyA, encrypted, iv }) => {
          const pair = keyPairRef.current;
          if (!pair) {
            setError("Missing local key pair.");
            resetRequest();
            return;
          }
          void (async () => {
            try {
              const peerPublicKey = await importPublicKey({ publicKey: publicKeyA });
              const accessKey = await decryptAccessKey({
                encrypted,
                iv,
                privateKey: pair.privateKey,
                peerPublicKey,
              });
              localStorage.accessKey = accessKey;
              navigate("/tables");
            } catch {
              setError("Could not decrypt the key.");
              resetRequest();
            }
          })();
        },
        onDenied: () => {
          setError("Request was denied.");
          requesterRef.current = null;
          keyPairRef.current = null;
          setStatus("idle");
          setPairingCode("");
        },
        onError: ({ message }) => {
          setError(message);
        },
        onDisconnected: () => {
          setError("Connection closed.");
          requesterRef.current = null;
          keyPairRef.current = null;
          setStatus("idle");
          setPairingCode("");
        },
      });
      requesterRef.current = handle;
      if (cancelledRef.current) {
        handle.cancel();
        requesterRef.current = null;
        return;
      }
    } catch {
      keyPairRef.current = null;
      setStatus("idle");
      setError("Could not start a key request.");
    } finally {
      startingRef.current = false;
    }
  }, [navigate, resetRequest]);

  if (status === "requesting" || status === "waiting") {
    return (
      <div className="flex w-full flex-1 flex-col justify-center px-8 py-8">
        <div className="mx-auto flex w-full max-w-sm flex-col items-center gap-4 text-center">
          {status === "waiting" ? (
            <>
              <p className="text-muted-foreground text-sm">
                Share this code so the key holder can confirm it is you.
              </p>
              <p className="font-mono text-4xl tracking-[0.3em]">{pairingCode}</p>
              <p className="text-muted-foreground text-sm">Waiting for approval…</p>
            </>
          ) : (
            <p className="text-muted-foreground text-sm">Connecting…</p>
          )}
          {error && (
            <p className="text-destructive text-sm" role="alert">
              {error}
            </p>
          )}
          <Button type="button" variant="outline" onClick={resetRequest}>
            Cancel
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex w-full flex-1 flex-col justify-center px-8 py-8">
      <form
        className="mx-auto flex w-full max-w-sm flex-col gap-4 text-left"
        onSubmit={(e) => {
          e.preventDefault();
          localStorage.accessKey = key;
          navigate("/tables");
        }}
      >
        <div className="grid gap-2">
          <Label htmlFor="access-key">Key</Label>
          <Input
            id="access-key"
            name="accessKey"
            type="password"
            value={key}
            onChange={(e) => setKey(e.target.value)}
            autoComplete="current-password"
          />
        </div>
        {error && (
          <p className="text-destructive text-sm" role="alert">
            {error}
          </p>
        )}
        <Button type="submit">Continue</Button>
        <Button type="button" variant="outline" onClick={() => void handleRequestKey()}>
          Request key
        </Button>
      </form>
    </div>
  );
}
